import apiService from "services/api";
import { useQuery } from "react-query";
import { useState, useCallback, useMemo } from "react";
import {
  CustomModal,
  CustomTable,
  ImageModal,
  CustomAlert,
  ProtectedImg,
  ProtectedLink,
} from "components";
import { Button } from "reactstrap";
import {
  leaveTableColumns,
  leaveTypes,
  otTableColumns,
  periodMap,
} from "../constants";
import { useNotificationContext } from "contexts";
import { FiEdit } from "react-icons/fi";
import { capitalize } from "utils/capitalize";
import { absTimeDifference } from "utils/timeDifference";
import { getFileExtension } from "utils/getFileExtension";
import { useForm } from "react-hook-form";
import moment from "moment";
const PendingApplicationsTemplate = ({ type }) => {
  const { data, isLoading, refetch } = useQuery("pendingLeave", () =>
    type === "leave"
      ? apiService.leave.pending()
      : apiService.ot.pending().then((otData) => {
          const [minDate, maxDate] = otData.reduce(
            (acc, curr) => {
              const { date } = curr;

              if (date < acc[0]) {
                acc[0] = date;
              }
              if (date > acc[1]) {
                acc[1] = date;
              }
              return acc;
            },
            [otData[0]?.date, otData[0]?.date],
          );
          return apiService.attendance
            .get(undefined, { date_from: minDate, date_to: maxDate })
            .then((attendanceData) => {
              const finalData = otData.map((item) => {
                const { date, staff } = item;
                const attendance = attendanceData.find(
                  (attendanceItem) =>
                    attendanceItem.staffId === staff.id &&
                    attendanceItem.date === date,
                );
                return {
                  ...item,
                  ...attendance,
                };
              });
              return finalData;
            });
        }),
  );
  console.log(data);

  const { createNotification } = useNotificationContext();
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [leaveToApprove, setLeaveToApprove] = useState({
    firstName: "",
    lastName: "",
    id: "",
  });
  const [imgExpanded, expandImage] = useState(false);
  const [displayAlert, setDisplayAlert] = useState("");
  const [approveAllAlert, setApproveAllAlert] = useState(false);
  const { register, getValues } = useForm({
    defaultValues: { approvedReason: "" },
  });

  const showDeleteModal = (leaveToApprove) => {
    setLeaveToApprove(leaveToApprove);
    setDisplayConfirmationModal(true);
  };

  // Hide the modal
  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  // Handle the actual deletion of the item
  const approveLeave = async () => {
    const apiCall =
      type === "OT" ? apiService.ot.approve : apiService.leave.approve;
    await apiCall(leaveToApprove.id, {
      approved: true,
      status: "Processed",
      approvedReason: getValues("approvedReason"),
    })
      .then(() => {
        createNotification({
          type: "success",
          message: "Successfully approved leave",
        });
        setDisplayConfirmationModal(false);
        hideAlert();
        refetch();
      })
      .catch((error) =>
        createNotification({ type: "danger", message: error.message }),
      );
  };

  const approveAllLeaves = async () => {
    const apiCall =
      type === "OT" ? apiService.ot.approveAll : apiService.leave.approveAll;
    await apiCall()
      .then(() => {
        createNotification({
          type: "success",
          message: "Successfully approved leave",
        });
        setApproveAllAlert(false);
        hideAlert();
        refetch();
      })
      .catch((error) =>
        createNotification({ type: "danger", message: error.message }),
      );
  };

  const hideAlert = useCallback(() => setDisplayAlert(""), []);
  const showAlert = (alertType) => setDisplayAlert(alertType);

  const disapproveLeave = useCallback(async () => {
    const apiCall =
      type === "OT" ? apiService.ot.update : apiService.leave.update;
    await apiCall(leaveToApprove.id, {
      approved: false,
      status: "Processed",
      approvedReason: getValues("approvedReason"),
    })
      .then(() => {
        createNotification({
          type: "success",
          message: "Successfully disapproved leave",
        });
        hideAlert();
        setDisplayConfirmationModal(false);
        refetch();
      })
      .catch((error) =>
        createNotification({
          type: "danger",
          message: error.message,
        }),
      );
  }, [leaveToApprove.id]);

  const actions = useCallback(
    (row) => (
      <div className="actions-right">
        <Button
          color="warning"
          size="sm"
          className="btn-icon btn-link edit"
          onClick={() => showDeleteModal(row)}
        >
          <FiEdit size={20} />
        </Button>
      </div>
    ),
    [],
  );

  const Modals = () => (
    <>
      <CustomAlert
        show={Boolean(displayAlert)}
        title={`${capitalize(displayAlert)} Confirmation`}
        submitButtonText="Confirm"
        hideModal={hideAlert}
        submitModal={
          displayAlert === "approve" ? approveLeave : disapproveLeave
        }
        body={useMemo(
          () => (
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">Message</span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Leave blank if None"
                {...register("approvedReason")}
              />
            </div>
          ),
          [],
        )}
      />

      <CustomAlert
        show={approveAllAlert}
        title={`Approve All ${capitalize(type)}?`}
        submitButtonText="Confirm"
        hideModal={() => setApproveAllAlert(false)}
        submitModal={approveAllLeaves}
      />

      <CustomModal
        title={`${capitalize(type)} application`}
        show={displayConfirmationModal}
        hideModal={hideConfirmationModal}
        submitButtonText="Approve"
        buttons={
          <>
            <Button onClick={() => showAlert("approve")} color="primary">
              Approve
            </Button>
            <Button onClick={() => showAlert("disapprove")} color="light">
              Disapprove
            </Button>
          </>
        }
      >
        <div className="row text-white px-4 text-left">
          <div className="col-12 col-sm-6 mb-4">
            <div className="font-weight-bold text-primary">Leave Date</div>
            <div>{new Date(leaveToApprove.date).toString().slice(0, 15)}</div>
          </div>
          <div className="col-12 col-sm-6 mb-4">
            <div className="font-weight-bold text-primary">Employee Name</div>
            <div>{leaveToApprove.staff?.fullName}</div>
          </div>
          {leaveToApprove.type === "CL" && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">CL Balance</div>
              <div>
                {leaveToApprove.staff?.compensationLeaveHoursCount} hours
              </div>
            </div>
          )}
          {leaveToApprove.type && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Type</div>
              <div>
                {
                  leaveTypes.find(
                    (leave) => leave.value === leaveToApprove.type,
                  )?.label
                }
              </div>
            </div>
          )}
          {leaveToApprove.period && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Period</div>
              <div>{periodMap[leaveToApprove.period]}</div>
            </div>
          )}
          {leaveToApprove.startTime && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Start</div>
              <div>{leaveToApprove.startTime}</div>
            </div>
          )}
          {leaveToApprove.endTime && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">End</div>
              <div>{leaveToApprove.endTime}</div>
            </div>
          )}
          {leaveToApprove.startTime && leaveToApprove.endTime && (
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Duration</div>
              <div>
                {(
                  absTimeDifference(
                    leaveToApprove.startTime,
                    leaveToApprove.endTime,
                  ) / 60
                ).toFixed(2)}{" "}
                hours
              </div>
            </div>
          )}
          {
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Arrival Time</div>
              <div>
                {leaveToApprove.arrivalTime
                  ? moment(
                      leaveToApprove.arrivalTime.split(".")[0],
                      "hh:mm:ss",
                    ).format("hh:mm:ss A")
                  : "NO CARD RECORDS"}
              </div>
            </div>
          }
          {
            <div className="col-12 col-sm-6 mb-4">
              <div className="font-weight-bold text-primary">Leaving Time</div>
              <div>
                {leaveToApprove.leavingTime
                  ? moment(
                      leaveToApprove.leavingTime.split(".")[0],
                      "hh:mm:ss",
                    ).format("hh:mm:ss A")
                  : "NO CARD RECORDS"}
              </div>
            </div>
          }
          {leaveToApprove.reason && (
            <div className="col-12 mb-4">
              <div className="font-weight-bold text-primary">Reason</div>
              <div>{leaveToApprove.reason}</div>
            </div>
          )}
          {leaveToApprove.proof && (
            <div className="col-12 text-left">
              <div className="font-weight-bold text-primary">Proof</div>
              {getFileExtension(leaveToApprove.proof) === "pdf" ? (
                <ProtectedLink
                  href={leaveToApprove.proof}
                  target="_blank"
                  className="badge badge-secondary"
                  label="Click To View Proof"
                />
              ) : (
                <ProtectedImg
                  src={leaveToApprove.proof}
                  alt=""
                  className="img-fluid"
                  onClick={expandImage}
                />
              )}
            </div>
          )}
        </div>

        <div className="mx-3 py-3 text-right">
          <small className="text-muted">
            Submitted at:{" "}
            {new Date(leaveToApprove.submittedAt).toLocaleString()}
          </small>
        </div>
      </CustomModal>

      <ImageModal open={imgExpanded} hideModal={() => expandImage(false)}>
        <ProtectedImg src={leaveToApprove.proof} alt="" className="img-fluid" />
      </ImageModal>
    </>
  );
  return (
    <div className="content">
      <div className="d-flex mb-2">
        <button
          className="btn btn-warning"
          onClick={() => setApproveAllAlert(true)}
        >
          Approve All
        </button>
      </div>
      <Modals />
      <CustomTable
        data={data}
        columns={type === "leave" ? leaveTableColumns : otTableColumns}
        defaultPageSize={25}
        title={capitalize(type) + "s"}
        actions={actions}
        loading={isLoading}
      />
    </div>
  );
};

export default PendingApplicationsTemplate;
